import React, { createContext, useContext, useReducer, useEffect, useCallback, useMemo, useRef } from 'react';
import { chatReducer, initialChatState } from './chatReducer';
import { loadDock, saveDock, clearDock } from './dockPersistence';
import { auth, db } from '../../../integrations/firebase';
import { signOut } from 'firebase/auth';
import { ref, set, serverTimestamp, push, get, child, onValue, update, onDisconnect } from 'firebase/database';
import { logger } from '../../../core/logger';
import { MessageService } from '../services/MessageService';
import { LogsService } from '../services/LogsService';
import { useToast } from '../../../shared/ui/Toast';
import { ChatNotificationManager } from '../ui/ChatNotificationToast';
import { useCallController } from '../calls/useCallController';

const ChatContext = createContext();

/** Like useChat, but null outside the provider — for shared screens that only need the signed-in user. */
export const useChatOptional = () => useContext(ChatContext);

export const useChat = () => {
    const context = useContext(ChatContext);
    if (!context) throw new Error('useChat must be used within ChatProvider');
    return context;
};

export const ChatProvider = ({ children }) => {
    const [chatState, chatDispatch] = useReducer(chatReducer, initialChatState);
    const { showToast } = useToast();

    const { currentUser, activeChats, minimizedChats, isMainChatOpen } = chatState;
    const unread = chatState.unread || {};

    // Refs for stability in effects/callbacks
    const currentUserRef = useRef(currentUser);
    const profileUnsubRef = useRef(null); // Hoisted for cleanup access
    const showToastRef = useRef(showToast);

    useEffect(() => {
        currentUserRef.current = currentUser;
        showToastRef.current = showToast;
    }, [currentUser, showToast]);


    const [initializing, setInitializing] = React.useState(true);


    // --- Auth & User Status Sync ---
    useEffect(() => {
        let isMounted = true;

        const unsubscribeAuth = auth.onAuthStateChanged(user => {
            if (!isMounted) return;

            if (user) {
                // Signed in — keep the app in the loading state until the profile is
                // fetched, so the route guard shows "Loading…" instead of bouncing to
                // /login during the gap (which forced users to log in twice).
                setInitializing(true);
                // 1. Initial Auth Set
                const userRef = ref(db, `users/${user.uid}`);

                // 2. Check/Create Profile
                // 2. Check/Create Profile
                get(userRef).then((snapshot) => {
                    if (!isMounted) return;
                    if (!snapshot.exists()) {
                        update(userRef, {
                            name: user.displayName || user.email.split('@')[0],
                            email: user.email,
                            photo: user.photoURL || '',
                            status: 'online',
                            lastSeen: serverTimestamp(),
                            activeDevice: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop'
                        });
                    } else {
                        update(userRef, {
                            status: 'online',
                            lastSeen: serverTimestamp(),
                            activeDevice: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop'
                        });
                    }
                });

                // 3. LISTEN TO REAL-TIME UPDATES (Profile Only)
                // Cleanup previous profile listener if re-authenticating
                if (profileUnsubRef.current) {
                    profileUnsubRef.current();
                    profileUnsubRef.current = null;
                }

                const attachProfile = () => {
                    if (profileUnsubRef.current) { profileUnsubRef.current(); profileUnsubRef.current = null; }
                    profileUnsubRef.current = onValue(userRef, (snapshot) => {
                        // Guard against a stale callback from a previous session's listener
                        // firing after a different user has since signed in.
                        if (!isMounted || auth.currentUser?.uid !== user.uid) return;
                        const data = snapshot.val();
                        if (data) {
                            chatDispatch({
                                type: 'SET_CURRENT_USER',
                                payload: { ...data, ...user, uid: user.uid, id: user.uid }
                            });
                            setInitializing(false);
                        }
                        // If data is null the get()->update() above is still creating the
                        // profile; onValue re-fires with data shortly. The watchdog below
                        // guards the rare case where that never happens.
                    }, (error) => {
                        // Profile read rejected. A genuinely role-less account must be
                        // signed out — but a LIVE session must never die on one transient
                        // denial: flaky networks and token refreshes cancel listeners with
                        // "permission denied" too, and acting on the first one was logging
                        // valid staff out several times a day.
                        if (!isMounted || auth.currentUser?.uid !== user.uid) return;
                        console.warn('[ChatContext] profile read denied for', user.uid, '- verifying before acting:', error?.message || error);
                        (async () => {
                            let confirmedDenied = false;
                            for (const delay of [1500, 5000]) {
                                await new Promise(r => setTimeout(r, delay));
                                if (!isMounted || auth.currentUser?.uid !== user.uid) return;
                                try {
                                    await user.getIdToken(true);          // force a fresh token first
                                    const snap = await get(userRef);
                                    if (snap.exists()) {                  // access is fine — it was a blip
                                        console.warn('[ChatContext] access verified after transient denial — staying signed in');
                                        attachProfile();                  // re-arm the live listener
                                        return;
                                    }
                                    confirmedDenied = true;               // read SUCCEEDED and the node is absent/denied
                                } catch (e) {
                                    // Only an explicit rules rejection counts as evidence.
                                    // A network failure, timeout or token hiccup proves
                                    // NOTHING about the account — never log out on it.
                                    const msg = String(e?.code || e?.message || '').toLowerCase();
                                    if (msg.includes('permission_denied') || msg.includes('permission denied')) confirmedDenied = true;
                                }
                            }
                            if (!isMounted || auth.currentUser?.uid !== user.uid) return;
                            if (!confirmedDenied) {
                                // Offline/flaky device (the "logged out very fast" report):
                                // keep the session, re-arm, and let the next success recover.
                                console.warn('[ChatContext] could not verify (network?) — KEEPING the session and re-arming');
                                attachProfile();
                                return;
                            }
                            // Confirmed with a fresh token: this account really has no role.
                            console.error('[ChatContext] profile read CONFIRMED denied for', user.uid, '- signing out');
                            try { sessionStorage.setItem('authDeniedReason', 'This account has no access role assigned yet. Please contact your administrator.'); } catch { /* ignore */ }
                            chatDispatch({ type: 'SET_CURRENT_USER', payload: null });
                            setInitializing(false);
                            try { signOut(auth); } catch { /* best effort */ }
                        })();
                    });
                };
                attachProfile();

                // 3b. Watchdog: never let the loader hang. If neither the profile listener
                // nor its error handler has resolved within 8s (slow network, stalled
                // socket), clear the loading gate so the user is never stuck on "Loading…".
                setTimeout(() => { if (isMounted) setInitializing(false); }, 8000);

            } else {
                // Dev Mode / Logout
                const devUser = localStorage.getItem('dev_mode_user');
                if (devUser) {
                    try {
                        const parsedUser = JSON.parse(devUser);
                        chatDispatch({ type: 'SET_CURRENT_USER', payload: parsedUser });
                    } catch (e) {
                        chatDispatch({ type: 'SET_CURRENT_USER', payload: null });
                    }
                } else {
                    chatDispatch({ type: 'SET_CURRENT_USER', payload: null });
                }
                setInitializing(false); // Stop loading immediately if no user
            }
        });

        return () => {
            isMounted = false;
            unsubscribeAuth();
            // Clean up hoisted profile listener
            if (profileUnsubRef.current) {
                profileUnsubRef.current();
                profileUnsubRef.current = null;
            }
        };
    }, []);

    // --- Global Data Listeners (Users/Agents) ---
    // Separated to not block Auth init and to be stable.
    useEffect(() => {
        const usersRef = ref(db, 'users');
        const agentsRef = ref(db, 'agents');

        const unsubUsers = onValue(usersRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const list = Object.keys(data).map(k => ({ ...data[k], id: k, type: 'user' }));
                chatDispatch({ type: 'SET_USERS', payload: list });
            }
        }, (err) => { /* denied when logged out / on the login page — harmless */ console.debug('[ChatContext] users listener:', err?.message); });

        const unsubAgents = onValue(agentsRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const list = Object.keys(data).map(k => ({ ...data[k], id: k, type: 'agent' }));
                chatDispatch({ type: 'SET_AGENTS', payload: list });
            }
        }, (err) => { /* denied when logged out / on the login page — harmless */ console.debug('[ChatContext] agents listener:', err?.message); });

        return () => {
            unsubUsers();
            unsubAgents();
        };
    }, []);

    // --- Deterministic Chat ID Logic ---
    const getDeterministicChatId = useCallback((uid1, uid2) => {
        if (!uid1 || !uid2) return null;
        return [uid1, uid2].sort().join('_');
    }, []);

    const resolveChatId = useCallback(async (targetUserId) => {
        const u = currentUserRef.current;
        if (!u) return null;
        return getDeterministicChatId(u.uid, targetUserId);
    }, [getDeterministicChatId]);

    const getOrCreateChat = useCallback(async (uid, targetId) => {
        return getDeterministicChatId(uid, targetId);
    }, [getDeterministicChatId]);

    const openChat = useCallback((targetUser) => {
        const u = currentUserRef.current;
        if (!u) return;

        // GROUP CHATS — preserve the group's actual chatId
        if (targetUser.type === 'group' || targetUser.isGroup) {
            const chatObj = {
                ...targetUser,
                id: targetUser.chatId || targetUser.id,
                chatId: targetUser.chatId || targetUser.id
            };
            chatDispatch({ type: 'OPEN_CHAT', payload: chatObj });
            return;
        }

        // 1:1 CHATS — use deterministic chatId
        const deterministicId = getDeterministicChatId(u.uid, targetUser.id);
        const chatObj = {
            ...targetUser,
            targetUserId: targetUser.id,
            id: deterministicId,
            chatId: deterministicId
        };
        chatDispatch({ type: 'OPEN_CHAT', payload: chatObj });
    }, [getDeterministicChatId]);

    /**
     * Put back the conversations this tab had open before the reload.
     *
     * Waits for `currentUser`: restoring windows before we know who is signed in could
     * show the previous user's conversations on a shared machine. Runs once per sign-in,
     * and a sign-OUT clears the dock for the same reason.
     */
    const dockRestored = useRef(false);
    /** chatId -> unread, accumulated across the per-conversation listeners. */
    const unreadMapRef = useRef({});
    useEffect(() => {
        if (!currentUser) {
            if (dockRestored.current) { dockRestored.current = false; clearDock(); }
            return;
        }
        if (dockRestored.current) return;
        dockRestored.current = true;
        const saved = loadDock();
        if (saved) chatDispatch({ type: 'RESTORE_DOCK', payload: saved });
    }, [currentUser]);

    // Persist after every change, but only once signed in — see above.
    useEffect(() => {
        if (!currentUser || !dockRestored.current) return;
        saveDock({ activeChats, minimizedChats });
    }, [currentUser, activeChats, minimizedChats]);

    const closeChat = useCallback((chatId) => {
        chatDispatch({ type: 'CLOSE_CHAT', payload: chatId });
    }, []);

    const minimizeChat = useCallback((chatId) => {
        chatDispatch({ type: 'MINIMIZE_CHAT', payload: chatId });
    }, []);

    const maximizeChat = useCallback((chatId) => {
        chatDispatch({ type: 'MAXIMIZE_CHAT', payload: chatId });
    }, []);

    /** Server counts in (from conversations/{id}/unread/{uid}). */
    const setUnread = useCallback((map) => {
        chatDispatch({ type: 'SET_UNREAD', payload: map || {} });
    }, []);

    /** Clear one badge immediately — it must not linger on an open conversation. */
    const markChatRead = useCallback((chatId) => {
        chatDispatch({ type: 'MARK_READ', payload: chatId });
    }, []);

    const loginUser = useCallback(async (email, password) => {
        try {
            const { signInWithEmailAndPassword } = await import('firebase/auth');
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            return userCredential.user;
        } catch (error) {
            console.error(error);
            throw error;
        }
    }, []);

    const subscribeToTyping = useCallback((chatId, callback) => {
        // Path: conversations/{chatId}/typing
        const typingRef = ref(db, `conversations/${chatId}/typing`);
        return onValue(typingRef, (snapshot) => {
            const data = snapshot.val() || {};
            // data is { uid1: true, uid2: true }
            const typingIds = Object.keys(data);
            callback(typingIds);
        });
    }, []);

    // --- Notification & Scheduler Listener (STABILIZED) ---
    useEffect(() => {
        if (!currentUser?.uid) return;

        // Depends ONLY on UID, not full object.
        const userId = currentUser.uid;
        const userChatsRef = ref(db, `users/${userId}/chats`);

        const cleanups = new Map();

        const unsubscribeNotifications = onValue(userChatsRef, (snapshot) => {
            if (!snapshot.exists()) return;

            const currentChatIds = new Set();
            snapshot.forEach((childSnap) => {
                const chatId = childSnap.val();
                currentChatIds.add(chatId);

                if (!cleanups.has(chatId)) {
                    const chatCleanups = [];

                    // 1. Last Message (Notifications)
                    const lastMessageRef = ref(db, `conversations/${chatId}/lastMessage`);
                    const unsubMsg = onValue(lastMessageRef, (msgSnap) => {
                        const me = currentUserRef.current; // access via ref
                        if (msgSnap.exists() && me) {
                            const lastMsg = msgSnap.val();
                            const isRecent = Date.now() - (lastMsg.timestamp || 0) < 5000;
                            const isMe = lastMsg.senderId === me.uid;
                            // "X created #channel" / "X added Y" are housekeeping lines, not
                            // messages — they used to pop a toast reading "Someone: [message]".
                            const isSystem = lastMsg.senderId === 'system' || lastMsg.type === 'system';
                            if (isRecent && !isMe && !isSystem) {
                                // Resolve chat + sender info for the notification
                                Promise.all([
                                    // Only the three fields the toast shows. This used to
                                    // read `conversations/${chatId}` — the WHOLE node,
                                    // every message in the thread — for a name and a type.
                                    get(ref(db, `conversations/${chatId}/name`)),
                                    // Just the name and photo — this read the sender's WHOLE
                                    // user record (chat list, call logs…) for every toast.
                                    get(ref(db, `users/${lastMsg.senderId}/name`)),
                                    get(ref(db, `conversations/${chatId}/type`)),
                                    get(ref(db, `conversations/${chatId}/photo`)),
                                    get(ref(db, `users/${lastMsg.senderId}/photo`)),
                                ]).then(([nameSnap, senderNameSnap, typeSnap, photoSnap, senderPhotoSnap]) => {
                                    const convo = {
                                        name: nameSnap.exists() ? nameSnap.val() : '',
                                        type: typeSnap.exists() ? typeSnap.val() : '',
                                        photo: photoSnap.exists() ? photoSnap.val() : null,
                                    };
                                    const isChannel = convo.type === 'channel';
                                    const isGroup = convo.type === 'group' || isChannel;
                                    const senderName = senderNameSnap.val() || lastMsg.senderName || 'Someone';
                                    const senderPhoto = senderPhotoSnap.val() || null;
                                    // lastMessage is stored WITHOUT a type, so this always fell
                                    // through to '[message]'. No type means plain text.
                                    const messageText = (!lastMsg.type || lastMsg.type === 'text') ? lastMsg.text
                                        : lastMsg.type === 'sticker' ? '🎨 Sticker'
                                        : lastMsg.type === 'image' ? '📷 Image'
                                        : lastMsg.type === 'file' ? '📎 File'
                                        : lastMsg.type === 'voice' ? '🎤 Voice'
                                        : '[message]';

                                    ChatNotificationManager.notify({
                                        senderName,
                                        message: messageText,
                                        chatId,
                                        chatName: isChannel ? `#${convo.name || 'channel'}` : isGroup ? (convo.name || 'Group') : senderName,
                                        photo: isGroup ? (convo.photo || null) : senderPhoto,
                                        isGroup,
                                        isChannel,
                                        targetUserId: lastMsg.senderId
                                    });
                                }).catch(() => {});
                            }
                        }
                    });
                    chatCleanups.push(unsubMsg);

                    // 1b. Unread badge for THIS chat. The counter is a single number, so a
                    // listener per conversation is cheap — and it is the only way a
                    // minimised bubble can show a badge without the list being mounted.
                    const unreadRef = ref(db, `conversations/${chatId}/unread/${userId}`);
                    const unsubUnread = onValue(unreadRef, (uSnap) => {
                        const n = Number(uSnap.val()) || 0;
                        unreadMapRef.current = { ...unreadMapRef.current, [chatId]: n };
                        chatDispatch({ type: 'SET_UNREAD', payload: unreadMapRef.current });
                    });
                    chatCleanups.push(unsubUnread);

                    // 2. Scheduled Messages Poller
                    const stopPolling = MessageService.pollScheduledMessages(chatId, async (msg, key) => {
                        console.log("Processing scheduled message:", key);
                        await MessageService.processScheduledMessage(chatId, key, msg);
                    });
                    chatCleanups.push(stopPolling);

                    cleanups.set(chatId, chatCleanups);
                }
            });

            // Cleanup removed
            for (const [chatId, cleanupFns] of cleanups.entries()) {
                if (!currentChatIds.has(chatId)) {
                    cleanupFns.forEach(fn => fn && fn());
                    cleanups.delete(chatId);
                }
            }
        });

        return () => {
            unsubscribeNotifications();
            for (const cleanupFns of cleanups.values()) {
                cleanupFns.forEach(fn => fn && fn());
            }
            cleanups.clear();
        };
    }, [currentUser?.uid]); // CRITICAL: Only depend on UID stability

    // --- Heartbeat ---
    useEffect(() => {
        if (!currentUser?.uid) return;

        const userId = currentUser.uid;
        const statusRef = ref(db, `users/${userId}/status`);
        const lastSeenRef = ref(db, `users/${userId}/lastSeen`);

        set(statusRef, 'online');
        set(lastSeenRef, serverTimestamp());

        const interval = setInterval(() => {
            set(lastSeenRef, serverTimestamp());
            set(statusRef, 'online');
        }, 15000);

        const connectedRef = ref(db, '.info/connected');
        const unsubConnected = onValue(connectedRef, (snap) => {
            if (snap.val() === true) {
                onDisconnect(statusRef).set('offline');
                onDisconnect(lastSeenRef).set(serverTimestamp());
            }
        });

        return () => {
            clearInterval(interval);
            unsubConnected();
        };
    }, [currentUser?.uid]);


    // --- Calls (one-to-one) ---
    // All call logic lives in calls/useCallController: ringing, answering, connecting,
    // the media controls and every way a call can end. The old code here passed the click
    // EVENT to toggleMute/toggleVideo (so you could never unmute or switch the camera off)
    // and dispatched reducer actions that did not exist (so the mute icon never changed).
    const allUsersRef = useRef([]);
    allUsersRef.current = chatState.allUsers || [];
    const resolveUser = useCallback((uid) => {
        const u = allUsersRef.current.find(x => x && (x.id === uid || x.uid === uid));
        return u ? { name: u.name || u.displayName || u.email || '', photo: u.photo || u.photoURL || null } : null;
    }, []);
    const calls = useCallController({ currentUser, resolveUser, chatIdFor: getDeterministicChatId, showToast });
    const {
        call: activeCall, localPreview: localStream, remoteStream, muted: isMuted, cameraOn: isVideoEnabled,
        startCall, answerCall, endCall, toggleMute, toggleVideo, shareScreen,
    } = calls;

    const value = useMemo(() => ({
        ...chatState,
        activeCall,
        callControls: calls,
        localStream,
        remoteStream,
        isMuted,
        isVideoEnabled,
        toggleMute,
        toggleVideo,
        shareScreen,
        openChat,
        closeChat,
        minimizeChat,
        unread,
        setUnread,
        markChatRead,
        maximizeChat,
        loginUser,
        resolveChatId,
        getOrCreateChat,
        startCall,
        answerCall,
        endCall,
        subscribeToTyping,
        setCurrentUser: (user) => chatDispatch({ type: 'SET_CURRENT_USER', payload: user }),
        initializing,
        isMockMode: false
    }), [chatState, activeCall, calls, localStream, remoteStream, isMuted, isVideoEnabled, toggleMute, toggleVideo, shareScreen, openChat, closeChat, minimizeChat, maximizeChat, unread, setUnread, markChatRead, loginUser, resolveChatId, getOrCreateChat, startCall, answerCall, endCall, initializing, subscribeToTyping]);

    return (
        <ChatContext.Provider value={value}>
            {children}
        </ChatContext.Provider>
    );
};
